import { useEffect } from 'react';

/**
 * Hook that listens for clipboard paste events and executes a callback with the pasted text
 * @param callback - Function to execute when paste occurs, receives the pasted text
 * @param enabled - Whether the listener is active (default: true)
 * @param skipInputs - Whether to skip paste events when focused on input/textarea/contenteditable elements (default: true)
 */
export function useClipboardPaste(callback: (text: string, event: ClipboardEvent) => void, enabled: boolean = true, skipInputs: boolean = true) {
	useEffect(() => {
		if (!enabled) return;

		const handlePaste = async (event: Event) => {
			const clipboardEvent = event as ClipboardEvent;

			// Skip if pasting into an input field
			if (skipInputs) {
				const target = event.target as HTMLElement;
				if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
					return;
				}
			}

			const text = clipboardEvent.clipboardData?.getData('text/plain');
			if (text) {
				callback(text, clipboardEvent);
			}
		};

		document.addEventListener('paste', handlePaste as EventListener);
		return () => {
			document.removeEventListener('paste', handlePaste as EventListener);
		};
	}, [callback, enabled, skipInputs]);
}
